import { useState, useEffect, useRef } from "react";

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
const tempCache = new Map();
const LS_KEY = "the99.cache.waterTemp";

function readPersistedTempC() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function persistTempC(valueC) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(valueC)); } catch {}
}

function getCacheKey(lat, lng) {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

function toFahrenheit(celsius) {
  return (celsius * 9) / 5 + 32;
}

function formatTemp(celsius, unit) {
  const value = unit === "C" ? celsius : toFahrenheit(celsius);
  return `${Math.round(value)}°${unit}`;
}

// Estimate water temp in Celsius based on latitude and day of year
function estimateWaterTempC(lat) {
  const now = new Date();
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
  
  // Rough seasonal oscillation
  const seasonFactorC = Math.sin((dayOfYear - 80) * Math.PI / 182) * 8.3;
  
  // Latitude-based base temp in C
  const latFactorC = Math.max(7 - Math.abs(lat) * 0.18, -2);
  
  return 16 + latFactorC + seasonFactorC;
}

export default function useWaterTemp(location, unit = "F", { isOffline = false } = {}) {
  const [tempC, setTempC] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshToggle, setRefreshToggle] = useState(0);
  const forceRefreshRef = useRef(false);

  const refresh = () => {
    forceRefreshRef.current = true;
    setRefreshToggle((c) => c + 1);
  };

  useEffect(() => {
    if (!location?.lat || !location?.lng) {
      setTempC(null);
      setError("GPS needed");
      setLoading(false);
      return;
    }

    const fetchTemp = async () => {
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      const cacheKey = getCacheKey(location.lat, location.lng);
      const cached = tempCache.get(cacheKey);
      setLoading(true);

      // If offline, return persisted value immediately
      if (isOffline || !navigator.onLine) {
        const persisted = readPersistedTempC();
        if (persisted !== null) {
          setTempC(persisted);
          setError(null);
        } else {
          setError("Offline");
        }
        setLoading(false);
        return;
      }

      // Check if cache is still valid
      if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setTempC(cached.valueC);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        // Try NOAA API first
        const response = await fetch(
          `https://api.weather.gov/points/${location.lat},${location.lng}`
        );

        if (!response.ok) {
          throw new Error("NOAA API failed");
        }

        const data = await response.json();
        const gridpointsUrl = data.properties?.forecastGridData;

        if (gridpointsUrl) {
          const gridResponse = await fetch(gridpointsUrl);
          const gridData = await gridResponse.json();
          
          // Try to extract water/ocean temperature if available
          const waterTemp = gridData.properties?.waterTemperature;
          
          if (waterTemp && waterTemp.values && waterTemp.values.length > 0) {
            const celsius = waterTemp.values[0].value;
            if (celsius !== null) {
              tempCache.set(cacheKey, {
                valueC: celsius,
                timestamp: Date.now(),
              });
              persistTempC(celsius);
              setTempC(celsius);
              setError(null);
              setLoading(false);
              return;
            }
          }

          // Fallback to estimated based on air temp if available
          const airTemp = gridData.properties?.temperature;
          if (airTemp && airTemp.values && airTemp.values.length > 0) {
            const celsius = airTemp.values[0].value;
            if (celsius !== null) {
              // Water temp is typically ~4°C cooler than air temp
              const estimatedWaterC = celsius - 4;
              tempCache.set(cacheKey, {
                valueC: estimatedWaterC,
                timestamp: Date.now(),
              });
              persistTempC(estimatedWaterC);
              setTempC(estimatedWaterC);
              setError(null);
              setLoading(false);
              return;
            }
          }
        }

        throw new Error("No data returned");
      } catch (err) {
        console.error("Water temp fetch error:", err);
        
        // Final fallback: estimate based on latitude/season
        const estimatedC = estimateWaterTempC(location.lat);
        tempCache.set(cacheKey, {
          valueC: estimatedC,
          timestamp: Date.now(),
        });
        persistTempC(estimatedC);
        setTempC(estimatedC);
        setError(null);
        setLoading(false);
      }
    };

    fetchTemp();
  }, [location?.lat, location?.lng, refreshToggle, isOffline]);

  if (error) {
    return {
      temp: null,
      error,
      label: error === "GPS needed" ? "GPS needed" : `--°${unit}`,
      status: "warn",
      loading,
      refresh,
    };
  }

  if (tempC === null) {
    return {
      temp: null,
      error: null,
      label: `--°${unit}`,
      status: "warn",
      loading,
      refresh,
    };
  }

  return {
    temp: unit === "C" ? Math.round(tempC) : Math.round(toFahrenheit(tempC)),
    tempC,
    unit,
    error: null,
    label: formatTemp(tempC, unit),
    status: "good",
    loading,
    refresh,
  };
}
